/**
 * Gift-incentive progress bar — live rendering for snippets/gift-incentive-bar.liquid.
 *
 * One script drives every [data-gift-bar] on the page (the PDP copy and the
 * inline-cart copy) from one cart total, so the two can never disagree.
 *
 * Total sources, in order of arrival:
 *   - the server-rendered data-cart-total (first paint, may be a cached page)
 *   - /cart.js, fetched on load and after any /cart/add|change|update|clear
 *   - window.NovominsPDP.setCartTotal(amount), which the inline cart calls
 *     after every refresh (chained, like js/pages/product-page/add-to-cart.js)
 *   - the theme's cart:open / cart:updated / cart:refresh events
 *
 * Goals carry data-threshold in minor units (pence) and --gib-pos, their
 * position on the rail. The fill is piecewise-linear between goals so the dot
 * meets each circle exactly as it unlocks; past the last goal it glides to the
 * end of the rail.
 *
 * Animations (Jeanette, 22 Oct): the next reward ripples until it unlocks,
 * the fill glides between states, an unlocking circle pops with a small
 * overshoot and its check badge springs in, and confetti bursts from it.
 * Unlock effects only fire on a change observed in-session, never on first
 * paint, and never under prefers-reduced-motion.
 */
(function () {
  if (window.__giftBarLoaded) return;
  window.__giftBarLoaded = true;

  var CONFETTI_COLOURS = ['#2a5bd8', '#6f93e8', '#a9c0f2', '#f2c14e', '#e8d9bf'];
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var totalPence = null;
  var refreshing = false;
  var refreshQueued = false;

  function bars() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-gift-bar]'));
  }

  function money(pence, symbol, whole) {
    var amount = pence / 100;
    var text = whole ? String(Math.round(amount)) : amount.toFixed(2);
    return (symbol || '£') + text;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function goalsOf(bar) {
    return Array.prototype.slice.call(bar.querySelectorAll('[data-gib-goal]')).map(function (el) {
      return {
        el: el,
        threshold: parseInt(el.getAttribute('data-threshold'), 10) || 0,
        pos: parseFloat((el.style.getPropertyValue('--gib-pos') || '0').replace('%', '')) || 0,
        name: el.getAttribute('data-goal-name') || ''
      };
    }).sort(function (a, b) { return a.threshold - b.threshold; });
  }

  // Fill percentage for a total, walking the goals as rail segments.
  function fillFor(total, goals) {
    if (!goals.length) return 0;
    var prevT = 0;
    var prevP = 0;
    for (var i = 0; i < goals.length; i++) {
      var g = goals[i];
      if (total < g.threshold) {
        var span = g.threshold - prevT;
        var frac = span > 0 ? (total - prevT) / span : 1;
        return prevP + (g.pos - prevP) * Math.max(0, Math.min(1, frac));
      }
      prevT = g.threshold;
      prevP = g.pos;
    }
    return 100; // every goal unlocked: run to the end of the rail
  }

  function render(bar, firstPaint) {
    var goals = goalsOf(bar);
    if (!goals.length) return;
    var total = totalPence === null ? (parseInt(bar.getAttribute('data-cart-total'), 10) || 0) : totalPence;
    var symbol = bar.getAttribute('data-currency') || '£';
    var msgEl = bar.querySelector('[data-gib-msg]');
    var fillEl = bar.querySelector('[data-gib-fill]');

    var next = null;
    goals.forEach(function (g) {
      var unlocked = total >= g.threshold;
      var wasUnlocked = g.el.classList.contains('is-unlocked');
      g.el.classList.toggle('is-unlocked', unlocked);
      g.el.classList.toggle('is-locked', !unlocked);
      var pill = g.el.querySelector('[data-gib-pill]');
      if (pill) pill.textContent = money(g.threshold, symbol, true);
      if (!unlocked && !next) next = g;
      if (unlocked && !wasUnlocked && !firstPaint) celebrate(bar, g.el);
    });
    goals.forEach(function (g) {
      g.el.classList.toggle('is-next', g === next);
    });

    if (fillEl) fillEl.style.width = fillFor(total, goals).toFixed(2) + '%';

    bar.classList.toggle('is-complete', !next);
    bar.classList.toggle('is-empty', total <= 0);

    if (msgEl) {
      var html;
      if (!next) {
        html = escapeHtml(bar.getAttribute('data-msg-complete') || 'Congratulations! All rewards unlocked');
      } else if (total <= 0) {
        // Default state: plain goal name (the Figma colours it only once progress starts).
        html = escapeHtml(bar.getAttribute('data-msg-default') || 'Spend [amount] for [goal]')
          .replace('[amount]', '<strong>' + money(next.threshold, symbol) + '</strong>')
          .replace('[goal]', escapeHtml(next.name));
      } else {
        html = escapeHtml(bar.getAttribute('data-msg-togo') || '[amount] to go for [goal]')
          .replace('[amount]', '<strong>' + money(next.threshold - total, symbol) + '</strong>')
          .replace('[goal]', '<span class="gib__goal-name">' + escapeHtml(next.name) + '</span>');
      }
      if (msgEl.innerHTML !== html) msgEl.innerHTML = html;
    }
  }

  function renderAll(firstPaint) {
    bars().forEach(function (bar) { render(bar, firstPaint); });
  }

  // Unlock: toast chip in the cart, then pop the circle, spring the badge,
  // burst confetti. The toast is shown once per unlock, not once per bar.
  var toastTimer = null;
  function toast(goalEl) {
    var el = document.querySelector('[data-cart-toast]');
    var text = goalEl.getAttribute('data-toast');
    if (!el || !text) return;
    el.textContent = text;
    el.hidden = false;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.classList.remove('is-visible');
      setTimeout(function () { el.hidden = true; }, 300);
    }, 3200);
  }

  function celebrate(bar, goalEl) {
    if (bar.getAttribute('data-context') === 'cart') toast(goalEl);
    try {
      document.dispatchEvent(new CustomEvent('giftbar:unlock', { detail: { goal: goalEl.getAttribute('data-gib-goal'), context: bar.getAttribute('data-context') } }));
    } catch (e) {}
    if (reduceMotion) return;
    goalEl.classList.remove('is-popping');
    void goalEl.offsetWidth; // restart the keyframes
    goalEl.classList.add('is-popping');
    setTimeout(function () { goalEl.classList.remove('is-popping'); }, 900);
    confetti(bar, goalEl);
  }

  function confetti(bar, goalEl) {
    if (!goalEl.animate) return;
    var circle = goalEl.querySelector('.gib__circle') || goalEl;
    var barBox = bar.getBoundingClientRect();
    var box = circle.getBoundingClientRect();
    var originX = box.left - barBox.left + box.width / 2;
    var originY = box.top - barBox.top + box.height / 2;
    var layer = document.createElement('span');
    layer.className = 'gib__confetti';
    layer.setAttribute('aria-hidden', 'true');
    bar.appendChild(layer);

    var count = 22;
    for (var i = 0; i < count; i++) {
      var piece = document.createElement('i');
      var size = 4 + Math.random() * 4;
      piece.style.cssText =
        'left:' + originX + 'px;top:' + originY + 'px;width:' + size + 'px;height:' + (size * (Math.random() > 0.5 ? 1 : 1.8)) + 'px;' +
        'background:' + CONFETTI_COLOURS[i % CONFETTI_COLOURS.length] + ';' +
        'border-radius:' + (Math.random() > 0.5 ? '50%' : '1px') + ';';
      layer.appendChild(piece);
      var angle = (-90 + (Math.random() * 120 - 60)) * Math.PI / 180; // mostly upward
      var distance = 50 + Math.random() * 70;
      var dx = Math.cos(angle) * distance;
      var dy = Math.sin(angle) * distance;
      var rot = (Math.random() * 540 - 270);
      piece.animate([
        { transform: 'translate(-50%, -50%) translate(0, 0) rotate(0deg)', opacity: 1 },
        { transform: 'translate(-50%, -50%) translate(' + (dx * 0.7) + 'px, ' + (dy * 0.9) + 'px) rotate(' + (rot * 0.6) + 'deg)', opacity: 1, offset: 0.55 },
        { transform: 'translate(-50%, -50%) translate(' + dx + 'px, ' + (dy + 60) + 'px) rotate(' + rot + 'deg)', opacity: 0 }
      ], { duration: 900 + Math.random() * 400, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'forwards' });
    }
    setTimeout(function () { if (layer.parentNode) layer.parentNode.removeChild(layer); }, 1500);
  }

  function setTotalPence(pence) {
    if (!isFinite(pence)) return;
    totalPence = Math.max(0, Math.round(pence));
    renderAll(false);
  }

  function refresh() {
    if (!bars().length) return;
    if (refreshing) { refreshQueued = true; return; }
    refreshing = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { setTotalPence(cart.total_price || 0); })
      .catch(function () {})
      .then(function () {
        refreshing = false;
        if (refreshQueued) { refreshQueued = false; refresh(); }
      });
  }

  // Same contract the inline cart already uses for the PDP bar: amount in
  // major units after every cart refresh. Chain onto whatever is there.
  var previousPdpApi = window.NovominsPDP;
  window.NovominsPDP = Object.assign({}, previousPdpApi, {
    setCartTotal: function (amount) {
      if (previousPdpApi && typeof previousPdpApi.setCartTotal === 'function') {
        previousPdpApi.setCartTotal(amount);
      }
      var parsed = Number(amount);
      if (isFinite(parsed)) setTotalPence(parsed * 100);
    }
  });

  window.NovominsGiftBar = { refresh: refresh, setTotalPence: setTotalPence, render: function () { renderAll(false); } };

  // Catch cart mutations whichever script makes them.
  if (window.fetch && !window.__giftBarFetchPatched) {
    window.__giftBarFetchPatched = true;
    var origFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)(\.js)?/i.test(url)) {
        promise.then(function () { setTimeout(refresh, 80); }).catch(function () {});
      }
      return promise;
    };
  }

  window.addEventListener('cart:open', refresh);
  document.addEventListener('cart:updated', refresh);
  document.addEventListener('cart:refresh', refresh);

  function start() {
    renderAll(true);
    refresh();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
